import { useState } from 'react';
import { Clock, Calendar, User, PlayCircle, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

export const AppointmentCard = ({
  appointment,
  onViewDetails,
  onStatusChange,
  showActions = true
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const statusConfig = {
    scheduled: {
      className: 'bg-blue-100 text-blue-800',
      label: 'Scheduled'
    },
    'in-progress': {
      className: 'bg-yellow-100 text-yellow-800',
      label: 'In Progress'
    },
    completed: {
      className: 'bg-green-100 text-green-800',
      label: 'Completed'
    },
    cancelled: {
      className: 'bg-red-100 text-red-800',
      label: 'Cancelled'
    },
    'no-show': {
      className: 'bg-gray-100 text-gray-800',
      label: 'No Show'
    }
  };

  // Fix for AppointmentCard.jsx
  // src/components/appointments/AppointmentCard.jsx
  const handleStartSession = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsLoading(true);
    try {
      // Add this log to see if status change is successful
      if (onStatusChange) {
        await onStatusChange('in-progress');
      }
      const sessionPath = `/counselor/sessions/${appointment._id}`;
      navigate(sessionPath);
    } catch (error) {
      console.error('Failed to start session:', error);
      setIsLoading(false); // Make sure we set loading to false on error
    }
  };
  const getStatusBadge = () => {
    const config = statusConfig[appointment.status] || statusConfig.scheduled;
    return (
      <span className={`px-2 py-1 text-xs rounded-full font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const canStartSession =
    (user?.role === "counselor") &&
    appointment.status === 'scheduled' &&
    appointment.counselor?._id === user?.id;

  const isUpcoming = new Date(appointment.date) >= new Date();

  return (
    <div
      onClick={() => onViewDetails(appointment._id)}
      className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow duration-200 overflow-hidden cursor-pointer"
    >
      {/* Header with status */}
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50/50 dark:bg-slate-800/50">
        {getStatusBadge()}
        {appointment.isUrgent && (
          <div className="flex items-center text-red-600 dark:text-red-400">
            <AlertCircle className="h-4 w-4 mr-1" />
            <span className="text-xs font-medium">Urgent</span>
          </div>
        )}
      </div>

      {/* Main content */}
      <div className="p-4">
        <div className="space-y-3">
          {/* Client/Counselor Info */}
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white">
                {user?.role === 'counselor'
                  ? appointment.client?.fullName
                  : appointment.counselor?.fullName}
              </h3>
              <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                <User className="h-4 w-4 mr-1" />
                {user?.role === 'counselor' ? 'Client' : 'Counselor'}
              </div>
            </div>
          </div>

          {/* Date & Time */}
          <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
            <div className="flex items-center">
              <Calendar className="h-4 w-4 mr-1" />
              {new Date(appointment.date).toLocaleDateString()}
            </div>
            <div className="flex items-center">
              <Clock className="h-4 w-4 mr-1" />
              {appointment.timeSlot}
            </div>
          </div>

          {/* Service Type */}
          <div className="text-sm text-gray-600 dark:text-gray-300">
            {appointment.serviceType.split('-').map(word =>
              word.charAt(0).toUpperCase() + word.slice(1)
            ).join(' ')}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-300">
            {appointment.sessionType?.charAt(0).toUpperCase() + appointment.sessionType?.slice(1)}
          </div>

        </div>

        {/* Actions */}
        {showActions && (
          <div className="mt-4 flex justify-end space-x-3">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onViewDetails(appointment._id);
              }}
              className="px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors duration-200"
            >
              View Details
            </button>

            {canStartSession && isUpcoming && (
              <button
                onClick={handleStartSession}
                disabled={isLoading}
                className="flex items-center px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors duration-200 disabled:bg-green-300"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Starting...
                  </>
                ) : (
                  <>
                    <PlayCircle className="h-4 w-4 mr-1" />
                    Start Session
                  </>
                )}
              </button>
            )}

            {appointment.status === 'in-progress' && user?.role === 'counselor' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onStatusChange('completed');
                }}
                className="flex items-center px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors duration-200"
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                Complete Session
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AppointmentCard;